import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Users, UserPlus, Trash2, Copy, Check, Shield, Clock, AlertTriangle, Bell, XCircle, Building2, Globe, Briefcase, Target, Tag, FileText, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import EmailTemplateEditor from '../components/EmailTemplateEditor';

export default function SettingsPage() {
  const { user, company, logout } = useAuth();
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [profile, setProfile] = useState(null);

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    const load = async () => {
      try {
        const [membersRes, profileRes] = await Promise.all([
          api.get('/team/members'),
          api.get('/company/profile').catch(() => ({ data: { profile: null } })),
        ]);
        setMembers(membersRes.data.members || []);
        setPendingInvites(membersRes.data.pending_invites || []);
        setProfile(profileRes.data.profile || null);
        if (isOwner) {
          try {
            const [logsRes, notifsRes] = await Promise.all([
              api.get('/audit-logs'),
              api.get('/auth/notifications'),
            ]);
            setAuditLogs(logsRes.data.logs || []);
            setNotifications(notifsRes.data.notifications || []);
          } catch {}
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [isOwner]);

  const dismissNotification = async (id) => {
    try {
      await api.put(`/auth/notifications/${id}/read`);
      setNotifications(n => n.filter(x => x.id !== id));
    } catch {}
  };

  const deleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await api.delete('/auth/account');
      toast.success(isOwner ? 'Unternehmen und alle Konten gelöscht' : 'Ihr Konto wurde gelöscht');
      logout();
      window.location.href = '/login';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Konto konnte nicht gelöscht werden');
    } finally {
      setDeleteLoading(false);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      const res = await api.post('/auth/invite', { email: inviteEmail.trim() });
      setInviteToken(res.data.invite_token);
      toast.success(`Einladung erstellt für ${inviteEmail}`);
      setInviteEmail('');
      const membersRes = await api.get('/team/members');
      setPendingInvites(membersRes.data.pending_invites || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Einladung fehlgeschlagen');
    } finally {
      setInviteLoading(false);
    }
  };

  const removeMember = async (memberId) => {
    try {
      await api.delete(`/team/members/${memberId}`);
      setMembers(m => m.filter(u => u.id !== memberId));
      toast.success('Mitglied entfernt');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mitglied konnte nicht entfernt werden');
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/invite/${inviteToken}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Einladungslink kopiert!');
  };

  const canInvite = (company?.max_users || 1) > members.length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Einstellungen</h1>
        <p className="text-muted-foreground mt-1">Verwalten Sie Ihr Team und Ihre Firmeneinstellungen</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile" data-testid="profile-tab"><Building2 className="w-4 h-4 mr-2" />Unternehmen</TabsTrigger>
          <TabsTrigger value="team" data-testid="team-tab"><Users className="w-4 h-4 mr-2" />Team</TabsTrigger>
          <TabsTrigger value="email-template" data-testid="email-template-tab"><Mail className="w-4 h-4 mr-2" />E-Mail-Vorlage</TabsTrigger>
          {isOwner && <TabsTrigger value="audit" data-testid="audit-tab"><Shield className="w-4 h-4 mr-2" />Audit-Protokoll</TabsTrigger>}
          <TabsTrigger value="account" data-testid="account-tab"><XCircle className="w-4 h-4 mr-2" />Konto</TabsTrigger>
        </TabsList>

        {/* Owner notifications for deleted members */}
        {isOwner && notifications.filter(n => !n.read).length > 0 && (
          <div className="space-y-2">
            {notifications.filter(n => !n.read).map(n => (
              <div key={n.id} className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800" data-testid={`notification-${n.id}`}>
                <Bell className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{n.title}</p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">{n.message}</p>
                  <p className="text-xs text-amber-500 mt-1">{new Date(n.created_at).toLocaleString('de-DE')}</p>
                </div>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-amber-600 hover:text-amber-800" onClick={() => dismissNotification(n.id)} data-testid={`dismiss-notification-${n.id}`}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Company Profile Tab */}
        <TabsContent value="profile" className="space-y-6" data-testid="company-profile-tab">
          {!profile ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Building2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Noch kein Unternehmensprofil vorhanden.</p>
                <p className="text-sm text-muted-foreground mt-1">Starten Sie das Onboarding, um Ihre Firmen-Website analysieren zu lassen.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-emerald-600" />
                        {profile.company_name || company?.name || 'Unternehmen'}
                      </CardTitle>
                      {profile.source_url && (
                        <a href={profile.source_url} target="_blank" rel="noopener noreferrer"
                          className="text-sm text-emerald-600 hover:underline flex items-center gap-1 mt-1">
                          <Globe className="w-3.5 h-3.5" /> {profile.source_url}
                        </a>
                      )}
                    </div>
                    {profile.industry && (
                      <Badge variant="outline" className="text-xs">{profile.industry}</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {profile.company_description && (
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" /> Beschreibung
                      </h3>
                      <p className="text-sm leading-relaxed">{profile.company_description}</p>
                    </div>
                  )}

                  {profile.value_proposition && (
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-1.5 flex items-center gap-1.5">
                        <Target className="w-3.5 h-3.5" /> Wertversprechen
                      </h3>
                      <p className="text-sm leading-relaxed">{profile.value_proposition}</p>
                    </div>
                  )}

                  <div className="grid gap-6 md:grid-cols-2">
                    {profile.products_services?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                          <Briefcase className="w-3.5 h-3.5" /> Produkte & Dienstleistungen
                        </h3>
                        <div className="flex flex-wrap gap-1.5">
                          {profile.products_services.map((p, i) => (
                            <Badge key={i} variant="secondary" className="text-xs font-normal">{p}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {profile.target_customers?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5" /> Zielkunden
                        </h3>
                        <div className="flex flex-wrap gap-1.5">
                          {profile.target_customers.map((t, i) => (
                            <Badge key={i} variant="secondary" className="text-xs font-normal">{t}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {profile.keywords?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5" /> Keywords
                      </h3>
                      <div className="flex flex-wrap gap-1.5">
                        {profile.keywords.map((k, i) => (
                          <Badge key={i} variant="outline" className="text-xs font-normal">{k}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {profile.icp_assessment && (
                    <div className="border-t pt-5">
                      <h3 className="text-sm font-semibold text-muted-foreground mb-3">Wunschkunden-Profil (ICP)</h3>
                      <div className="grid gap-3 md:grid-cols-2">
                        {profile.icp_assessment.target_region && (
                          <div className="p-3 rounded-lg bg-muted/50">
                            <p className="text-xs text-muted-foreground">Zielregion</p>
                            <p className="text-sm font-medium mt-0.5">{Array.isArray(profile.icp_assessment.target_region) ? profile.icp_assessment.target_region.join(', ') : profile.icp_assessment.target_region}</p>
                          </div>
                        )}
                        {profile.icp_assessment.target_industry && (
                          <div className="p-3 rounded-lg bg-muted/50">
                            <p className="text-xs text-muted-foreground">Zielbranche</p>
                            <p className="text-sm font-medium mt-0.5">{Array.isArray(profile.icp_assessment.target_industry) ? profile.icp_assessment.target_industry.join(', ') : profile.icp_assessment.target_industry}</p>
                          </div>
                        )}
                        {profile.icp_assessment.target_company_size && (
                          <div className="p-3 rounded-lg bg-muted/50">
                            <p className="text-xs text-muted-foreground">Unternehmensgröße</p>
                            <p className="text-sm font-medium mt-0.5">{Array.isArray(profile.icp_assessment.target_company_size) ? profile.icp_assessment.target_company_size.join(', ') : profile.icp_assessment.target_company_size}</p>
                          </div>
                        )}
                        {profile.icp_assessment.target_customer_type && (
                          <div className="p-3 rounded-lg bg-muted/50">
                            <p className="text-xs text-muted-foreground">Kundentyp</p>
                            <p className="text-sm font-medium mt-0.5">{Array.isArray(profile.icp_assessment.target_customer_type) ? profile.icp_assessment.target_customer_type.join(', ') : profile.icp_assessment.target_customer_type}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {profile.updated_at && (
                    <p className="text-xs text-muted-foreground pt-2 border-t">
                      Zuletzt aktualisiert: {new Date(profile.updated_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Teammitglieder</CardTitle>
                <CardDescription>{members.length} von {company?.max_users || 1} Plätzen belegt</CardDescription>
              </div>
              {isOwner && (
                <Button onClick={() => setShowInviteDialog(true)} disabled={!canInvite} data-testid="invite-member-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Mitglied einladen
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {!canInvite && isOwner && (
                <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-700 dark:text-amber-300">Nutzerlimit erreicht. Upgraden Sie Ihren Plan, um weitere Mitglieder einzuladen.</p>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead>Rolle</TableHead>
                    <TableHead>Beigetreten</TableHead>
                    {isOwner && <TableHead className="w-[60px]"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map(m => (
                    <TableRow key={m.id} data-testid={`member-${m.id}`}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell className="text-muted-foreground">{m.email}</TableCell>
                      <TableCell><Badge variant={m.role === 'owner' ? 'default' : 'secondary'}>{m.role}</Badge></TableCell>
                      <TableCell className="text-muted-foreground text-sm">{new Date(m.created_at).toLocaleDateString()}</TableCell>
                      {isOwner && (
                        <TableCell>
                          {m.role !== 'owner' && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {pendingInvites.length > 0 && (
                <div className="mt-6">
                  <p className="text-sm font-medium mb-3">Ausstehende Einladungen</p>
                  <div className="space-y-2">
                    {pendingInvites.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50" data-testid={`pending-invite-${inv.id}`}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{inv.email}</span>
                        </div>
                        <Badge variant="outline">Ausstehend</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email-template">
          <EmailTemplateEditor user={user} company={company} />
        </TabsContent>

        {isOwner && (
          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Audit-Protokoll</CardTitle>
                <CardDescription>Verfolgen Sie alle wichtigen Aktionen in Ihrem Workspace</CardDescription>
              </CardHeader>
              <CardContent>
                {auditLogs.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">Noch keine Audit-Einträge</p>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {auditLogs.map(log => (
                      <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`audit-log-${log.id}`}>
                        <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{log.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                          {log.details && <p className="text-xs text-muted-foreground">{log.details}</p>}
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="account">
          <Card className="border-destructive/20">
            <CardHeader>
              <CardTitle className="text-lg text-destructive">Konto löschen</CardTitle>
              <CardDescription>
                {isOwner
                  ? 'Als Inhaber wird beim Löschen Ihres Kontos das gesamte Unternehmen einschließlich aller Teammitglieder, Daten und Suchläufe unwiderruflich gelöscht.'
                  : 'Beim Löschen Ihres Kontos werden Sie aus dem Unternehmen entfernt. Der Inhaber wird über Ihren Austritt informiert.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isOwner && members.length > 1 && (
                <div className="mb-4 p-3 rounded-lg bg-destructive/5 border border-destructive/20 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-destructive">Achtung: {members.length - 1} weitere{members.length - 1 === 1 ? 's' : ''} Teammitglied{members.length - 1 === 1 ? '' : 'er'} betroffen</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {members.filter(m => m.role !== 'owner').map(m => m.name).join(', ')} — diese Konten werden ebenfalls gelöscht.
                    </p>
                  </div>
                </div>
              )}
              <Button variant="destructive" onClick={() => setShowDeleteDialog(true)} data-testid="delete-account-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                {isOwner ? 'Unternehmen und Konto löschen' : 'Mein Konto löschen'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Account Tab Content - rendered outside Tabs for proper dialog nesting */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { setShowDeleteDialog(open); if (!open) setDeleteConfirmText(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {isOwner ? 'Unternehmen und Konto endgültig löschen' : 'Konto endgültig löschen'}
            </DialogTitle>
            <DialogDescription>
              {isOwner ? (
                <>
                  <span className="block mt-2 font-semibold text-destructive">Diese Aktion kann nicht rückgängig gemacht werden!</span>
                  <span className="block mt-2">Als Inhaber werden beim Löschen Ihres Kontos auch <strong>alle Teammitglieder</strong>, das gesamte Unternehmensprofil, alle Suchläufe, Leads und Outreach-Entwürfe unwiderruflich gelöscht.</span>
                </>
              ) : (
                <>
                  <span className="block mt-2">Ihr Konto wird unwiderruflich gelöscht. Der Inhaber des Unternehmens wird über Ihren Austritt informiert.</span>
                  <span className="block mt-2 font-semibold text-destructive">Diese Aktion kann nicht rückgängig gemacht werden!</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Geben Sie <span className="font-mono font-bold">LÖSCHEN</span> ein, um zu bestätigen</Label>
              <Input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="LÖSCHEN"
                data-testid="delete-confirm-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeleteConfirmText(''); }}>
                Abbrechen
              </Button>
              <Button
                variant="destructive"
                disabled={deleteConfirmText !== 'LÖSCHEN' || deleteLoading}
                onClick={deleteAccount}
                data-testid="confirm-delete-account-btn"
              >
                {deleteLoading ? 'Wird gelöscht...' : isOwner ? 'Unternehmen löschen' : 'Konto löschen'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Teammitglied einladen</DialogTitle>
            <DialogDescription>Senden Sie einen Einladungslink an ein neues Teammitglied</DialogDescription>
          </DialogHeader>
          {!inviteToken ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>E-Mail-Adresse</Label>
                <Input placeholder="kollege@firma.de" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                  data-testid="invite-email-input" onKeyDown={e => e.key === 'Enter' && sendInvite()} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Abbrechen</Button>
                <Button onClick={sendInvite} disabled={inviteLoading} data-testid="send-invite-btn">
                  {inviteLoading ? 'Wird gesendet...' : 'Einladung senden'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Teilen Sie diesen Einladungslink mit dem neuen Teammitglied:</p>
              <div className="flex gap-2">
                <Input readOnly value={`${window.location.origin}/invite/${inviteToken}`} className="font-mono text-xs" data-testid="invite-link-input" />
                <Button variant="outline" onClick={copyInviteLink} data-testid="copy-invite-link-btn">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => { setShowInviteDialog(false); setInviteToken(null); }}>Fertig</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
